import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import WeibullSurvivabilityCard from './Chart';
import { useAppSelector } from 'store/hook';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import SearchableSelector from 'components/SearchableSelector';
import SurvivalAnalysisOverviewHandler from 'handlers/survivalAnalysisHandlers/survivalAnalysisOverview.handler';
import {
  getUniqueMakeModels,
  getUniqueComponents,
  getUniqueSubComponents,
  exportWeibullCSV,
} from './utils';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import type { AssetWeibullMaintenanceStatus } from './types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const Survivability = () => {
  const { t } = useTranslation();
  const survivalAnalysisHandler = new SurvivalAnalysisOverviewHandler();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { data, workOrders, isLoadingSurvivability } = useAppSelector(
    (state) => state.survivabilityReducer
  );
  const assetList = useAppSelector((state) => state.assetReducer.assets);
  const [selectedComponent, setSelectedComponent] = useState<string>('');
  const [selectedSubComponent, setSelectedSubComponent] = useState<string>('');
  const [showPopup, setShowPopup] = useState<{
    makeModel: string;
    data: AssetWeibullMaintenanceStatus[];
  } | null>(null);

  const fontColor = palette.textPrimary;

  const cellStyle = {
    color: fontColor,
    borderBottom: '1px solid #2F445E',
  };

  const handleComponentSelection = (value: string) => {
    setSelectedComponent(value);
  };

  const handleSubComponentSelection = (value: string) => {
    setSelectedSubComponent(value);
  };

  const fetchData = async () => {
    void survivalAnalysisHandler.getSurvivalSurvivability();
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const componentOptions = useMemo(() => {
    return getUniqueComponents(data);
  }, [data]);

  const makeModels = useMemo(() => {
    return getUniqueMakeModels(data, selectedComponent, selectedSubComponent);
  }, [data, selectedComponent, selectedSubComponent]);

  useEffect(() => {
    setSelectedComponent(componentOptions[0]);
  }, [componentOptions]);

  const subComponentOptions = useMemo(() => {
    return getUniqueSubComponents(data, selectedComponent);
  }, [selectedComponent]);

  useEffect(() => {
    setSelectedSubComponent(subComponentOptions[0]);
  }, [subComponentOptions]);

  const exportCSV = () => {
    if (showPopup) {
      exportWeibullCSV(showPopup?.data, `${showPopup?.makeModel}`);
    }
    setShowPopup(null);
  };

  return (
    <>
      {isLoadingSurvivability ? (
        <Box
          sx={{
            paddingTop: '150px',
          }}
        >
          <SingleFadeLoader isFullScreen={true} />
        </Box>
      ) : (
        <>
          {componentOptions.length === 0 ? (
            <Typography
              sx={{
                color: fontColor,
                fontSize: '14px',
              }}
            >
              {t('no_survivability_data')}
            </Typography>
          ) : (
            <Box>
              <Dialog
                open={showPopup !== null}
                onClose={() => {
                  setShowPopup(null);
                }}
                maxWidth="md"
                fullWidth
                sx={{
                  '& .MuiDialog-paper': {
                    backgroundColor: enableDarkTheme
                      ? palette.boxBackground
                      : palette.boxBackground,
                    color: enableDarkTheme
                      ? ThemePalette.typography.wheat
                      : ThemePalette.typography.black,
                  },
                }}
              >
                <DialogTitle
                  sx={{
                    borderBottom: '1px solid #2F445E',
                  }}
                >
                  {t('eol_assets')} {showPopup?.makeModel}
                </DialogTitle>

                {showPopup?.data?.length === 0 ? (
                  <Typography
                    sx={{
                      padding: '10px',
                      color: fontColor,
                      fontSize: '14px',
                    }}
                  >
                    {' '}
                    {t('no_assets_data_to_display')}
                  </Typography>
                ) : (
                  <>
                    <DialogContent sx={{ marginBottom: '10px', padding: 0 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={cellStyle}>
                              {t('asset_id')}
                            </TableCell>
                            <TableCell align="center" sx={cellStyle}>
                              {t('days_since_repair')}
                            </TableCell>
                            <TableCell align="center" sx={cellStyle}>
                              {t('days_since_replace')}
                            </TableCell>
                            <TableCell align="center" sx={cellStyle}>
                              {t('days_till_next_repair')}
                            </TableCell>
                            <TableCell align="center" sx={cellStyle}>
                              {t('days_till_next_replace')}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {showPopup?.data.map((item, index: number) => {
                            return (
                              <TableRow key={index}>
                                <TableCell sx={cellStyle}>
                                  {item.asset_id}
                                </TableCell>
                                <TableCell align="center" sx={cellStyle}>
                                  {item.daysSinceLastRepair ?? '--'}
                                </TableCell>
                                <TableCell align="center" sx={cellStyle}>
                                  {item.daysSinceLastReplace ?? '--'}
                                </TableCell>
                                <TableCell align="center" sx={cellStyle}>
                                  <Typography
                                    sx={{
                                      color: fontColor,
                                      fontSize: '14px',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {item.daysUntilNextRepair
                                      ? `${item.daysUntilNextRepair}`
                                      : '--'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center" sx={cellStyle}>
                                  <Typography
                                    sx={{
                                      color: fontColor,
                                      fontSize: '14px',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {item.daysUntilNextReplace
                                      ? `${item.daysUntilNextReplace}`
                                      : '--'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </DialogContent>
                    <DialogActions
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Button
                        onClick={() => {
                          setShowPopup(null);
                        }}
                        sx={{
                          textTransform: 'none',
                          padding: '5px 20px 5px 20px',
                          border: '1px solid #2F445E',
                          color: fontColor,
                        }}
                      >
                        {t('close')}
                      </Button>
                      <Button
                        onClick={exportCSV}
                        sx={{
                          textTransform: 'none',
                          padding: '5px 20px 5px 20px',
                          border: '1px solid #2F445E',
                          color: fontColor,
                        }}
                      >
                        {t('export_csv')}
                      </Button>
                    </DialogActions>
                  </>
                )}
              </Dialog>
              <Box
                sx={{
                  borderRadius: '4px',
                  border: '1px solid #2F445E',
                  padding: '10px',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: fontColor,
                    marginRight: '5px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {t('component')}
                </Typography>
                <SearchableSelector
                  options={[...componentOptions]}
                  selectedValues={[selectedComponent]}
                  onSelectionChange={handleComponentSelection}
                  placeholder={t('search_components')}
                  label={t('component')}
                  multiple={false}
                  showSelectAll={false}
                />

                <Typography
                  sx={{
                    fontSize: '14px',
                    color: fontColor,
                    marginLeft: '10px',
                    marginRight: '5px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {t('sub_component')}
                </Typography>
                <SearchableSelector
                  options={[...subComponentOptions]}
                  selectedValues={[selectedSubComponent]}
                  onSelectionChange={handleSubComponentSelection}
                  placeholder={t('search_sub_component')}
                  label={t('sub_component_2')}
                  multiple={false}
                  showSelectAll={false}
                />
              </Box>
              <Grid container spacing={1}>
                {makeModels.map((item, index) => {
                  return (
                    <Grid item key={index} lg={6} md={6} sm={6}>
                      <WeibullSurvivabilityCard
                        enableDarkTheme={enableDarkTheme}
                        makeModel={item}
                        data={data}
                        selectedComponent={selectedComponent}
                        selectedSubComponent={selectedSubComponent}
                        workOrders={workOrders}
                        assets={assetList}
                        setShowPopup={setShowPopup}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default Survivability;
